/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetSlackDirectoryMemberRequest,
  GetSlackDirectoryMemberSecurity,
} from "../models/operations/getslackdirectorymember.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSlackDirectoryMemberQuery,
  prefetchSlackDirectoryMember,
  queryKeySlackDirectoryMember,
  SlackDirectoryMemberQueryData,
} from "./slackDirectoryMember.core.js";
export {
  buildSlackDirectoryMemberQuery,
  prefetchSlackDirectoryMember,
  queryKeySlackDirectoryMember,
  type SlackDirectoryMemberQueryData,
};

export type SlackDirectoryMemberQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMember slackDirectoryConnections
 *
 * @remarks
 * Read current Slack profile and mapping before an administrator confirms a selection.
 */
export function useSlackDirectoryMember(
  request: GetSlackDirectoryMemberRequest,
  security?: GetSlackDirectoryMemberSecurity | undefined,
  options?: QueryHookOptions<
    SlackDirectoryMemberQueryData,
    SlackDirectoryMemberQueryError
  >,
): UseQueryResult<
  SlackDirectoryMemberQueryData,
  SlackDirectoryMemberQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSlackDirectoryMemberQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMember slackDirectoryConnections
 *
 * @remarks
 * Read current Slack profile and mapping before an administrator confirms a selection.
 */
export function useSlackDirectoryMemberSuspense(
  request: GetSlackDirectoryMemberRequest,
  security?: GetSlackDirectoryMemberSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SlackDirectoryMemberQueryData,
    SlackDirectoryMemberQueryError
  >,
): UseSuspenseQueryResult<
  SlackDirectoryMemberQueryData,
  SlackDirectoryMemberQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSlackDirectoryMemberQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSlackDirectoryMemberData(
  client: QueryClient,
  queryKeyBase: [parameters: { id: string; gramSession?: string | undefined }],
  data: SlackDirectoryMemberQueryData,
): SlackDirectoryMemberQueryData | undefined {
  const key = queryKeySlackDirectoryMember(...queryKeyBase);

  return client.setQueryData<SlackDirectoryMemberQueryData>(key, data);
}

export function invalidateSlackDirectoryMember(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "slackDirectoryConnections",
      "getMember",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllSlackDirectoryMember(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "slackDirectoryConnections", "getMember"],
  });
}
